// =====================================================================
// PLENARIUM - Eleicao da Mesa Diretora: secao do admin na tela da sessao
// (PLENARIUM:ELEICAO_MESA). Componente independente: conversa so com
// /api/eleicoes e escuta o aviso 'eleicao-atualizada' da sala da sessao.
// =====================================================================
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { io } from 'socket.io-client';
import { Award, Lock, Unlock, Play, Square, Plus, X, Trash2, FileText, RefreshCw } from 'lucide-react';

const CARGOS_PADRAO = ['Presidente', 'Vice-Presidente', '1º Secretário', '2º Secretário'];
const STATUS = {
  CADASTRO: { txt: 'NÃO INICIADA', cls: 'badge-cinza' },
  EM_VOTACAO: { txt: 'EM VOTAÇÃO', cls: 'badge-vermelho' },
  EMPATE: { txt: 'EMPATE · AGUARDANDO DEFINIÇÃO', cls: 'badge-amarelo' },
  CONCLUIDA: { txt: 'CONCLUÍDA', cls: 'badge-verde' }
};
// PLENARIUM:ELEICAO_VISUAL -- cores da situacao e visual dos cartoes
const ST_COR = {
  CADASTRO: { rot: 'Não iniciada', cor: '#64748b', fundo: 'rgba(100,116,139,.12)' },
  EM_VOTACAO: { rot: 'Em votação', cor: '#dc2626', fundo: 'rgba(220,38,38,.12)' },
  EMPATE: { rot: 'Empate · aguardando definição', cor: '#b45309', fundo: 'rgba(217,119,6,.14)' },
  CONCLUIDA: { rot: 'Concluída', cor: '#15803d', fundo: 'rgba(22,163,74,.13)' }
};
const CSS_ELEICAO = `
.emv-card { padding: 0 !important; overflow: hidden; border-left: 4px solid var(--cinza); }
.emv-cab { display: flex; align-items: flex-start; gap: 14px; padding: 16px 18px; }
.emv-ic { width: 46px; height: 46px; border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.emv-sup { font-size: .7rem; font-weight: 800; letter-spacing: .8px; text-transform: uppercase; color: var(--acento, #2563eb); }
.emv-tit { font-size: 1.1rem; font-weight: 800; color: var(--texto); margin: 1px 0 4px; }
.emv-meta { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; font-size: .75rem; color: var(--texto-suave); }
.emv-tag { display: inline-flex; align-items: center; gap: 4px; border: 1px solid var(--cinza); border-radius: 999px; padding: 1px 8px; font-weight: 600; }
.emv-status { display: inline-flex; align-items: center; gap: 6px; border-radius: 999px; padding: 5px 12px; font-size: .74rem; font-weight: 800; white-space: nowrap; flex-shrink: 0; }
.emv-pulso { width: 7px; height: 7px; border-radius: 50%; background: currentColor; animation: emvPulso 1.2s ease infinite; }
@keyframes emvPulso { 0%,100% { opacity: 1; } 50% { opacity: .25; } }
.emv-eleita { margin: 0 18px 14px; display: flex; align-items: center; gap: 14px; padding: 14px 16px; border-radius: 14px; background: linear-gradient(135deg, rgba(22,163,74,.12), rgba(22,163,74,.04)); border: 1px solid rgba(22,163,74,.35); }
.emv-eleita-num { width: 46px; height: 46px; border-radius: 50%; color: #fff; font-weight: 900; font-size: 1.25rem; display: flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 0 4px 12px rgba(15,23,42,.18); }
.emv-eleita-rot { font-size: .7rem; font-weight: 800; letter-spacing: .8px; text-transform: uppercase; color: #15803d; }
.emv-eleita-nome { font-size: 1.15rem; font-weight: 800; color: var(--texto); }
.emv-eleita-obs { font-size: .75rem; color: var(--texto-suave); margin-top: 2px; }
.emv-eleita-votos { text-align: right; flex-shrink: 0; }
.emv-eleita-votos b { display: block; font-size: 1.6rem; line-height: 1; color: #15803d; font-variant-numeric: tabular-nums; }
.emv-eleita-votos span { font-size: .72rem; color: var(--texto-suave); }
.emv-chapas { display: grid; grid-template-columns: repeat(auto-fill, minmax(250px, 1fr)); gap: 12px; padding: 0 18px 16px; }
.emv-chapa { border: 1px solid var(--cinza); border-top: 4px solid var(--cor); border-radius: 12px; padding: 12px 14px; background: var(--branco, transparent); }
.emv-chapa.eleita { border-color: rgba(22,163,74,.55); border-top-color: var(--cor); box-shadow: 0 0 0 2px rgba(22,163,74,.18); }
.emv-chapa.empate { border-color: #d97706; box-shadow: 0 0 0 2px rgba(217,119,6,.18); }
.emv-chapa-cab { display: flex; align-items: center; gap: 8px; }
.emv-chapa-num { width: 28px; height: 28px; border-radius: 50%; color: #fff; font-weight: 900; font-size: .85rem; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.emv-chapa-nome { font-weight: 800; color: var(--texto); flex: 1; min-width: 0; overflow-wrap: anywhere; }
.emv-selo { font-size: .66rem; font-weight: 800; text-transform: uppercase; letter-spacing: .5px; border-radius: 999px; padding: 2px 8px; }
.emv-selo.ok { color: #15803d; background: rgba(22,163,74,.14); }
.emv-selo.emp { color: #b45309; background: rgba(217,119,6,.14); }
.emv-chapa-votos { margin-top: 10px; font-size: .78rem; color: var(--texto-suave); }
.emv-chapa-votos b { color: var(--texto); font-size: .95rem; }
.emv-barra { height: 7px; border-radius: 999px; background: var(--cinza); overflow: hidden; margin-bottom: 5px; }
.emv-barra span { display: block; height: 100%; border-radius: 999px; transition: width .4s; }
.emv-membros { margin-top: 10px; display: flex; flex-direction: column; gap: 6px; }
.emv-membro { display: flex; flex-direction: column; padding-left: 10px; border-left: 2px solid var(--cinza); }
.emv-membro small { font-size: .66rem; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; color: var(--texto-suave); }
.emv-membro b { font-size: .85rem; color: var(--texto); }
.emv-chapa-acoes { display: flex; gap: 4px; margin-top: 10px; padding-top: 8px; border-top: 1px dashed var(--cinza); }
.emv-nova { border: 2px dashed var(--cinza); background: none; border-radius: 12px; min-height: 140px; color: var(--texto-suave); font: inherit; font-weight: 700; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 6px; }
.emv-nova:hover { border-color: var(--acento, #2563eb); color: var(--acento, #2563eb); }
.emv-and { padding: 14px 18px 16px; border-top: 1px solid var(--cinza); }
.emv-part-txt { display: flex; justify-content: space-between; flex-wrap: wrap; gap: 6px; font-size: .82rem; color: var(--texto-suave); margin-bottom: 6px; }
.emv-part-txt b { color: var(--texto); }
.emv-obs { display: flex; align-items: center; gap: 6px; font-size: .78rem; color: var(--texto-suave); margin-top: 6px; }
.emv-sec { font-size: .7rem; font-weight: 800; letter-spacing: .8px; text-transform: uppercase; color: var(--texto-suave); margin: 14px 0 8px; }
.emv-nominais { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 6px; }
.emv-nominal { display: flex; align-items: center; justify-content: space-between; gap: 8px; border: 1px solid var(--cinza); border-radius: 10px; padding: 6px 10px; }
.emv-nominal-nome { font-size: .8rem; font-weight: 600; color: var(--texto); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.emv-nominal-voto { font-size: .7rem; font-weight: 800; border-radius: 999px; padding: 2px 8px; white-space: nowrap; flex-shrink: 0; }
.emv-acoes { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; padding: 10px 18px; border-top: 1px solid var(--cinza); background: var(--cinza-claro, rgba(148,163,184,.06)); }
.emv-link { display: inline-flex; align-items: center; gap: 5px; background: none; border: none; padding: 5px 8px; border-radius: 8px; font: inherit; font-size: .78rem; font-weight: 600; color: var(--texto-suave); cursor: pointer; }
.emv-link:hover { background: var(--cinza); color: var(--texto); }
.emv-link.perigo:hover { color: var(--reprovado); }
@media (max-width: 640px) { .emv-eleita { flex-wrap: wrap; } .emv-eleita-votos { text-align: left; } }
`;
const CORES = ['#2563eb', '#0d9488', '#c2410c', '#7c3aed', '#be185d', '#4d7c0f'];
const nomeV = (v) => (v ? (v.nomeParlamentar || v.nome) : '');
const erroDe = (e, padrao) => e?.response?.data?.error || padrao;

export default function EleicaoMesaAdmin({ sessaoId, sessaoStatus, vereadores = [] }) {
  const [lista, setLista] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [ocupado, setOcupado] = useState(false);
  const [modalConfig, setModalConfig] = useState(null);   // { id?, bienio, votoSecreto, cargosTxt }
  const [modalChapa, setModalChapa] = useState(null);     // { eleicao, id?, nome, membros: {cargo: vereadorId} }
  const [modalEmpate, setModalEmpate] = useState(null);   // { eleicao, chapaId, fundamento }
  const socketRef = useRef(null);

  const carregar = useCallback(async () => {
    try {
      const r = await axios.get(`/api/eleicoes/sessao/${sessaoId}`);
      setLista(r.data || []);
    } catch { /* silencioso: a secao so nao aparece */ }
    finally { setCarregando(false); }
  }, [sessaoId]);

  useEffect(() => {
    carregar();
    const socket = io(process.env.REACT_APP_API_URL || 'http://localhost:3001');
    socketRef.current = socket;
    let primeira = true;
    socket.on('connect', () => {
      socket.emit('entrar-sala', `sessao-${sessaoId}`);
      if (!primeira) carregar();
      primeira = false;
    });
    socket.on('eleicao-atualizada', (d) => { if (!d || d.sessaoId === sessaoId) carregar(); });
    return () => socket.disconnect();
  }, [sessaoId, carregar]);

  const acao = async (fn, ok) => {
    if (ocupado) return;
    setOcupado(true);
    try { await fn(); if (ok) toast.success(ok); await carregar(); }
    catch (e) {
      if (e?.response?.status === 409) { window.alert('⚠️ ' + erroDe(e, 'A situação mudou.')); await carregar(); }
      else toast.error(erroDe(e, 'Não foi possível concluir.'));
    }
    finally { setOcupado(false); }
  };

  // ---------------- configuracao ----------------
  const salvarConfig = () => {
    const m = modalConfig;
    const cargos = m.cargosTxt.split('\n').map(s => s.trim()).filter(Boolean);
    if (!m.bienio.trim()) return toast.error('Informe o biênio.');
    if (!cargos.length) return toast.error('Informe ao menos um cargo.');
    const body = { bienio: m.bienio.trim(), votoSecreto: m.votoSecreto, cargos };
    acao(async () => {
      if (m.id) await axios.put(`/api/eleicoes/${m.id}`, body);
      else await axios.post('/api/eleicoes', { ...body, sessaoId });
      setModalConfig(null);
    }, m.id ? 'Eleição atualizada.' : 'Eleição criada.');
  };

  // ---------------- chapas ----------------
  const abrirChapa = (el, chapa) => {
    const membros = {};
    (chapa?.membros || []).forEach(m => { membros[m.cargo] = m.vereadorId; });
    setModalChapa({ eleicao: el, id: chapa?.id, nome: chapa?.nome || '', membros });
  };
  const salvarChapa = () => {
    const m = modalChapa;
    if (!m.nome.trim()) return toast.error('Informe o nome da chapa.');
    const membros = m.eleicao.cargos.filter(c => m.membros[c]).map(c => ({ cargo: c, vereadorId: m.membros[c] }));
    if (!membros.length) return toast.error('Escolha ao menos um membro.');
    if (new Set(membros.map(x => x.vereadorId)).size !== membros.length) return toast.error('O mesmo vereador está em dois cargos.');
    acao(async () => {
      if (m.id) await axios.put(`/api/eleicoes/chapas/${m.id}`, { nome: m.nome.trim(), membros });
      else await axios.post(`/api/eleicoes/${m.eleicao.id}/chapas`, { nome: m.nome.trim(), membros });
      setModalChapa(null);
    }, m.id ? 'Chapa atualizada.' : 'Chapa cadastrada.');
  };
  const excluirChapa = (c) => {
    if (!window.confirm(`Excluir a chapa "${c.nome}"?`)) return;
    acao(() => axios.delete(`/api/eleicoes/chapas/${c.id}`), 'Chapa excluída.');
  };

  // ---------------- votacao ----------------
  const abrir = (el) => {
    if (sessaoStatus !== 'EM_ANDAMENTO') return toast.error('Inicie a sessão antes de abrir a votação da eleição.');
    if (!window.confirm(`Abrir a votação da eleição da Mesa (biênio ${el.bienio})?\n\n${el.chapas.length} chapa(s) · ${el.votoSecreto ? 'voto SECRETO' : 'voto ABERTO'}\n\nDepois de aberta, as chapas não podem mais ser alteradas.`)) return;
    acao(() => axios.patch(`/api/eleicoes/${el.id}/abrir`), 'Votação da eleição aberta!');
  };
  const cancelar = (el) => {
    if (!window.confirm('Ninguém votou ainda. Voltar a eleição para o cadastro (para corrigir as chapas)?')) return;
    acao(() => axios.patch(`/api/eleicoes/${el.id}/cancelar`), 'Abertura cancelada.');
  };
  const encerrar = async (el) => {
    // Busca o numero mais recente e mostra na confirmacao; o servidor confere.
    let atual = el;
    try { atual = (await axios.get(`/api/eleicoes/${el.id}`)).data; } catch {}
    const faltam = Math.max(0, (presentesOuTotal(atual)) - atual.votaram);
    const msg = (faltam > 0 ? `⚠️ ATENÇÃO: ${faltam} vereador(es) ainda não votaram.\n\n` : '') +
      `Encerrar a votação da eleição?\n\nVotaram: ${atual.votaram}` +
      (atual.votoSecreto ? '\n(Voto secreto: o resultado aparece depois de encerrar.)' : '\n' + atual.chapas.map(c => `Chapa ${c.numero} · ${c.nome}: ${c.votos}`).join('\n') + `\nAbstenções: ${atual.abstencoes}`);
    if (!window.confirm(msg)) return;
    acao(() => axios.patch(`/api/eleicoes/${el.id}/encerrar`, { votaram: atual.votaram }), 'Votação da eleição encerrada.');
  };
  const presentesOuTotal = (el) => el.totalVereadores || vereadores.length;

  const definir = () => {
    const m = modalEmpate;
    if (!m.chapaId) return toast.error('Escolha a chapa eleita.');
    const c = m.eleicao.chapas.find(x => x.id === m.chapaId);
    if (!window.confirm(`Confirmar a Chapa ${c.numero} · ${c.nome} como ELEITA?\n\nA decisão fica registrada na ata com seu nome e horário.`)) return;
    acao(async () => { await axios.patch(`/api/eleicoes/${m.eleicao.id}/definir`, { chapaId: m.chapaId, fundamento: m.fundamento }); setModalEmpate(null); }, 'Chapa eleita definida.');
  };

  if (carregando) return null;
  const temAtiva = lista.some(e => e.status !== 'CONCLUIDA');
  const podeCriar = sessaoStatus !== 'ENCERRADA' && !temAtiva;
  if (!lista.length && sessaoStatus === 'ENCERRADA') return null;

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ fontSize: '0.95rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}><Award size={16} /> Eleição da Mesa Diretora</h2>
        {podeCriar && (
          <button className="btn btn-secondary btn-sm" onClick={() => setModalConfig({ bienio: '', votoSecreto: true, cargosTxt: CARGOS_PADRAO.join('\n') })}>
            <Plus size={13} /> {lista.length ? 'Nova eleição' : 'Criar eleição da Mesa'}
          </button>
        )}
      </div>

      {!lista.length && (
        <div className="card sem-dados"><p>Nenhuma eleição da Mesa nesta sessão. Use “Criar eleição da Mesa” apenas na sessão de eleição.</p></div>
      )}

      <style>{CSS_ELEICAO}</style>{/* PLENARIUM:ELEICAO_VISUAL */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {lista.map(el => {
          const votaramIds = new Set(el.votantes.map(v => v.vereadorId));
          const faltam = vereadores.filter(v => !votaramIds.has(v.id));
          const eleita = el.chapas.find(c => c.id === el.chapaEleitaId);
          const total = presentesOuTotal(el);
          const mostraVotos = el.status !== 'CADASTRO' && el.chapas.some(c => c.votos !== null && c.votos !== undefined);
          const pct = (n) => (el.votaram ? Math.round(((n || 0) / el.votaram) * 100) : 0);
          const corChapa = (id) => { const k = el.chapas.findIndex(c => c.id === id); return k >= 0 ? CORES[k % CORES.length] : '#94a3b8'; };
          const corSt = ST_COR[el.status] || ST_COR.CADASTRO;
          return (
            <div key={el.id} className="card emv-card" style={{ borderLeftColor: corSt.cor }}>
              {/* Cabecalho */}
              <div className="emv-cab">
                <div className="emv-ic" style={{ background: corSt.fundo, color: corSt.cor }}><Award size={22} /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="emv-sup">Eleição da Mesa Diretora</div>
                  <div className="emv-tit">Biênio {el.bienio}</div>
                  <div className="emv-meta">
                    <span className="emv-tag">{el.votoSecreto ? <><Lock size={11} /> Voto secreto</> : <><Unlock size={11} /> Voto aberto</>}</span>
                    <span>{el.cargos.join(' · ')}</span>
                  </div>
                </div>
                <span className={`emv-status ${el.status === 'EM_VOTACAO' ? 'vivo' : ''}`} style={{ color: corSt.cor, background: corSt.fundo }}>
                  {el.status === 'EM_VOTACAO' && <span className="emv-pulso" />}{corSt.rot}
                </span>
              </div>

              {/* Resultado em destaque */}
              {el.status === 'CONCLUIDA' && eleita && (
                <div className="emv-eleita">
                  <span className="emv-eleita-num" style={{ background: corChapa(eleita.id) }}>{eleita.numero}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="emv-eleita-rot">Chapa eleita</div>
                    <div className="emv-eleita-nome">{eleita.nome}</div>
                    {el.desempatePor && <div className="emv-eleita-obs">Definida em caso de empate por {el.desempatePor}{el.desempateFundamento ? ` — ${el.desempateFundamento}` : ''}</div>}
                  </div>
                  {mostraVotos && eleita.votos !== null && (
                    <div className="emv-eleita-votos"><b>{eleita.votos}</b><span>voto{eleita.votos === 1 ? '' : 's'} · {pct(eleita.votos)}%</span></div>
                  )}
                </div>
              )}

              {/* Chapas */}
              <div className="emv-chapas">
                {el.chapas.map((c, i) => {
                  const cor = CORES[i % CORES.length];
                  const destaque = c.id === el.chapaEleitaId;
                  const empatada = el.empatadas.includes(c.id) && el.status === 'EMPATE';
                  return (
                    <div key={c.id} className={`emv-chapa ${destaque ? 'eleita' : ''} ${empatada ? 'empate' : ''}`} style={{ '--cor': cor }}>
                      <div className="emv-chapa-cab">
                        <span className="emv-chapa-num" style={{ background: cor }}>{c.numero}</span>
                        <span className="emv-chapa-nome">{c.nome}</span>
                        {destaque && <span className="emv-selo ok">Eleita</span>}
                        {empatada && <span className="emv-selo emp">Empate</span>}
                      </div>
                      {mostraVotos && c.votos !== null && (
                        <div className="emv-chapa-votos">
                          <div className="emv-barra"><span style={{ width: `${pct(c.votos)}%`, background: cor }} /></div>
                          <span><b>{c.votos}</b> voto{c.votos === 1 ? '' : 's'} · {pct(c.votos)}%</span>
                        </div>
                      )}
                      <div className="emv-membros">
                        {c.membros.map(m => (
                          <div key={m.cargo} className="emv-membro"><small>{m.cargo}</small><b>{m.nome}</b></div>
                        ))}
                      </div>
                      {el.status === 'CADASTRO' && (
                        <div className="emv-chapa-acoes">
                          <button className="emv-link" disabled={ocupado} onClick={() => abrirChapa(el, c)}><FileText size={13} /> Editar</button>
                          <button className="emv-link perigo" disabled={ocupado} onClick={() => excluirChapa(c)}><Trash2 size={13} /> Excluir</button>
                        </div>
                      )}
                    </div>
                  );
                })}
                {el.status === 'CADASTRO' && (
                  <button className="emv-nova" onClick={() => abrirChapa(el, null)} disabled={ocupado}><Plus size={16} /> Nova chapa</button>
                )}
              </div>

              {/* Andamento */}
              {el.status !== 'CADASTRO' && (
                <div className="emv-and">
                  <div className="emv-part">
                    <div className="emv-part-txt">
                      <span><b>{el.votaram}</b> de {total} vereador(es) votaram</span>
                      {el.abstencoes !== null && <span>{el.status === 'EM_VOTACAO' ? 'Abstenções até agora' : 'Abstenções'}: <b>{el.abstencoes}</b></span>}
                    </div>
                    <div className="emv-barra"><span style={{ width: `${total ? Math.min(100, Math.round((el.votaram / total) * 100)) : 0}%`, background: 'var(--acento, #2563eb)' }} /></div>
                  </div>
                  {el.totaisOcultos && <div className="emv-obs"><Lock size={12} /> Voto secreto: os votos por chapa ficam ocultos até o encerramento.</div>}
                  {el.status === 'EM_VOTACAO' && faltam.length > 0 && <div className="emv-obs">Faltam votar: {faltam.map(nomeV).join(', ')}</div>}
                  {!el.votoSecreto && el.votosAbertos.length > 0 && (
                    <>
                      <div className="emv-sec">Votos nominais</div>
                      <div className="emv-nominais">
                        {el.votosAbertos.map(v => {
                          const c = el.chapas.find(x => x.id === v.chapaId);
                          const cor = c ? corChapa(c.id) : '#94a3b8';
                          return (
                            <div key={v.vereadorId} className="emv-nominal">
                              <span className="emv-nominal-nome">{v.nome}</span>
                              <span className="emv-nominal-voto" style={{ color: cor, background: c ? cor + '1a' : 'rgba(148,163,184,.15)' }}>{c ? `Chapa ${c.numero}` : 'Abstenção'}</span>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Acoes */}
              {sessaoStatus !== 'ENCERRADA' && el.status !== 'CONCLUIDA' && (
                <div className="emv-acoes">
                  {el.status === 'CADASTRO' && (
                    <>
                      <button className="btn btn-primary btn-sm" disabled={ocupado || !el.chapas.length} onClick={() => abrir(el)}><Play size={13} /> Abrir votação da eleição</button>
                      <span style={{ flex: 1 }} />
                      <button className="emv-link" disabled={ocupado} onClick={() => setModalConfig({ id: el.id, bienio: el.bienio, votoSecreto: el.votoSecreto, cargosTxt: el.cargos.join('\n') })}><FileText size={13} /> Configurar</button>
                      <button className="emv-link perigo" disabled={ocupado} onClick={() => { if (window.confirm('Excluir esta eleição e todas as chapas?')) acao(() => axios.delete(`/api/eleicoes/${el.id}`), 'Eleição excluída.'); }}><Trash2 size={13} /> Excluir eleição</button>
                    </>
                  )}
                  {el.status === 'EM_VOTACAO' && (
                    <>
                      <button className="btn btn-danger btn-sm" disabled={ocupado} onClick={() => encerrar(el)}><Square size={13} /> Encerrar votação da eleição</button>
                      <span style={{ flex: 1 }} />
                      {el.votaram === 0 && <button className="emv-link" disabled={ocupado} onClick={() => cancelar(el)}><RefreshCw size={13} /> Cancelar abertura</button>}
                    </>
                  )}
                  {el.status === 'EMPATE' && (
                    <button className="btn btn-primary btn-sm" disabled={ocupado} onClick={() => setModalEmpate({ eleicao: el, chapaId: '', fundamento: '' })}><Award size={13} /> Definir chapa eleita</button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Modal configuracao */}
      {modalConfig && (
        <div className="modal-overlay" onClick={() => setModalConfig(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <h2 className="modal-title"><Award size={16} /> {modalConfig.id ? 'Configurar eleição' : 'Criar eleição da Mesa'}</h2>
              <button className="btn-modal-close" onClick={() => setModalConfig(null)}><X size={18} /></button>
            </div>
            <div className="form-group">
              <label className="form-label">Biênio</label>
              <input className="form-input" value={modalConfig.bienio} placeholder="Ex: 2027–2028" onChange={e => setModalConfig({ ...modalConfig, bienio: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Tipo de voto (conforme o Regimento Interno)</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className={`btn btn-sm ${modalConfig.votoSecreto ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setModalConfig({ ...modalConfig, votoSecreto: true })}><Lock size={12} /> Secreto</button>
                <button type="button" className={`btn btn-sm ${!modalConfig.votoSecreto ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setModalConfig({ ...modalConfig, votoSecreto: false })}><Unlock size={12} /> Aberto</button>
              </div>
              <p style={{ fontSize: '0.72rem', color: 'var(--texto-suave)', marginTop: 6 }}>
                {modalConfig.votoSecreto ? 'Secreto: ninguém vê em quem cada vereador votou — nem o sistema guarda essa informação.' : 'Aberto: a TV, a ata e o relatório mostram o voto de cada vereador.'}
              </p>
            </div>
            <div className="form-group">
              <label className="form-label">Cargos da Mesa (um por linha)</label>
              <textarea className="form-input" rows={5} value={modalConfig.cargosTxt} onChange={e => setModalConfig({ ...modalConfig, cargosTxt: e.target.value })} />
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setModalConfig(null)}>Cancelar</button>
              <button className="btn btn-primary" disabled={ocupado} onClick={salvarConfig}>Salvar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal chapa */}
      {modalChapa && (
        <div className="modal-overlay" onClick={() => setModalChapa(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <h2 className="modal-title">{modalChapa.id ? 'Editar chapa' : 'Nova chapa'}</h2>
              <button className="btn-modal-close" onClick={() => setModalChapa(null)}><X size={18} /></button>
            </div>
            <div className="form-group">
              <label className="form-label">Nome da chapa</label>
              <input className="form-input" value={modalChapa.nome} placeholder="Ex: Chapa União" onChange={e => setModalChapa({ ...modalChapa, nome: e.target.value })} />
            </div>
            {modalChapa.eleicao.cargos.map(cargo => (
              <div className="form-group" key={cargo}>
                <label className="form-label">{cargo}</label>
                <select className="form-input form-select" value={modalChapa.membros[cargo] || ''} onChange={e => setModalChapa({ ...modalChapa, membros: { ...modalChapa.membros, [cargo]: e.target.value } })}>
                  <option value="">— escolher —</option>
                  {vereadores.map(v => <option key={v.id} value={v.id}>{nomeV(v)}{v.partido ? ` (${v.partido})` : ''}</option>)}
                </select>
              </div>
            ))}
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setModalChapa(null)}>Cancelar</button>
              <button className="btn btn-primary" disabled={ocupado} onClick={salvarChapa}>Salvar chapa</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal empate */}
      {modalEmpate && (
        <div className="modal-overlay" onClick={() => setModalEmpate(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <h2 className="modal-title"><Award size={16} /> Definir chapa eleita</h2>
              <button className="btn-modal-close" onClick={() => setModalEmpate(null)}><X size={18} /></button>
            </div>
            <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e', borderRadius: 8, padding: 10, fontSize: '0.82rem', marginBottom: 12 }}>
              Houve empate. Escolha a chapa eleita conforme o Regimento Interno. A decisão fica registrada na ata, com seu nome e o horário.
            </div>
            {modalEmpate.eleicao.chapas.filter(c => modalEmpate.eleicao.empatadas.includes(c.id)).map(c => (
              <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, border: `2px solid ${modalEmpate.chapaId === c.id ? 'var(--aprovado)' : 'var(--cinza)'}`, background: modalEmpate.chapaId === c.id ? '#f0fdf4' : '#fff', borderRadius: 10, padding: 12, marginBottom: 8, cursor: 'pointer' }}>
                <input type="radio" name="chapaEleita" checked={modalEmpate.chapaId === c.id} onChange={() => setModalEmpate({ ...modalEmpate, chapaId: c.id })} />
                <b>Chapa {c.numero} · {c.nome}</b>
                <span style={{ marginLeft: 'auto', fontFamily: 'monospace', fontWeight: 800 }}>{c.votos}</span>
              </label>
            ))}
            <div className="form-group">
              <label className="form-label">Fundamento (opcional, vai para a ata)</label>
              <textarea className="form-input" rows={3} value={modalEmpate.fundamento} placeholder="Ex: Art. 12 do Regimento Interno — eleita a chapa cujo candidato a Presidente for o mais idoso." onChange={e => setModalEmpate({ ...modalEmpate, fundamento: e.target.value })} />
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setModalEmpate(null)}>Cancelar</button>
              <button className="btn btn-primary" disabled={ocupado} onClick={definir}>Confirmar chapa eleita</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
